import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import DataTable from 'react-data-table-component';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { modernPalette as c, modernTableStyles, modernCardStyle } from '../Utils/customTableStyles';
import AdminHeader from '../Header/AdminHeader';
import AdminLeftMenu from '../Navbar/AdminLeftMenu';
import { userloginDetails } from '../Utils/AdminAuth';
import axios from 'axios';
import { STAGE_URL } from '../Dashboard/api/Api';
import switchStyles from './AiBot.module.css';
import styles from './AiBotUsageOverview.module.css';
import AiBotErrorLogs from './AiBotErrorLogs';

const ADMIN_USAGE_OVERVIEW_API = `${STAGE_URL}/admin-usage-overview`;
const ADMIN_SEARCH_API = `${STAGE_URL}/admin-usage-overview/search`;
const AI_BOT_TOGGLE_API = `${STAGE_URL}/ai-bot-access`;

const RANGE_PRESETS = [
    { value: 'today', label: 'Today' },
    { value: 'thisWeek', label: 'This Week' },
    { value: 'thisMonth', label: 'This Month' },
    { value: 'last3Months', label: 'Last 3 Months' },
    { value: 'custom', label: 'Custom Range' },
    { value: 'allTime', label: 'All Time' },
];

const selectStyles = {
    control: (provided) => ({
        ...provided,
        borderRadius: '20px',
        minHeight: '36px',
        cursor: 'pointer',
        fontSize: '13px',
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    option: (style) => ({
        ...style,
        fontSize: '13px',
        cursor: 'pointer',
    }),
};

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

// Resolves a preset key into a concrete { from, to } pair. Custom/all-time
// have no fixed range — custom comes from the date pickers, all-time sends
// no filter at all.
const resolvePresetRange = (preset) => {
    const now = new Date();
    switch (preset) {
        case 'today':
            return { from: startOfDay(now), to: now };
        case 'thisWeek': {
            const dayIndex = now.getDay();
            const daysSinceMonday = dayIndex === 0 ? 6 : dayIndex - 1;
            const monday = new Date(now);
            monday.setDate(now.getDate() - daysSinceMonday);
            return { from: startOfDay(monday), to: now };
        }
        case 'thisMonth':
            return { from: new Date(now.getFullYear(), now.getMonth(), 1), to: now };
        case 'last3Months':
            return { from: new Date(now.getFullYear(), now.getMonth() - 3, now.getDate()), to: now };
        default:
            return { from: null, to: null };
    }
};

// Real admins have numeric AuthKey ids; demo widget sessions log usage under
// a frontend-generated visitor id (e.g. "demo-visitor-ed06...") — never a
// row in aibotaccesses, so it has no enable/disable status to show.
const isDemoAdminId = (adminId) => Number.isNaN(Number(adminId));

const AiBotUsageOverview = () => {
    const lStorage = userloginDetails();
    const adminUserId = lStorage?.user?.userId;
    const adminToken = lStorage?.token;

    const history = useHistory();
    const location = useLocation();
    // Tab lives in the URL (?tab=usage|errors), same pattern this codebase
    // already uses for UserPriceDetails' channel param — so switching tabs is
    // a real navigation (back/forward and reload both land on the right tab),
    // not just local state.
    const activeTab = new URLSearchParams(location.search).get('tab') === 'errors' ? 'errors' : 'usage';
    const setActiveTab = (tab) => history.push({ pathname: location.pathname, search: `?tab=${tab}` });

    const [admins, setAdmins] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [preset, setPreset] = useState('thisMonth');
    const [activeRange, setActiveRange] = useState(resolvePresetRange('thisMonth'));
    const [customFrom, setCustomFrom] = useState(null);
    const [customTo, setCustomTo] = useState(null);
    const [showDemoUsers, setShowDemoUsers] = useState(false);
    const [togglingId, setTogglingId] = useState(null);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [total, setTotal] = useState(0);
    const [resetToken, setResetToken] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [error, setError] = useState({
        errorStatus: false,
        bgColor: '',
        errorMessage: ''
    });
    const searchWrapRef = useRef(null);

    useEffect(() => {
        fetchOverview({ range: resolvePresetRange('thisMonth'), page: 1, limit: perPage, includeDemoUsers: false });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Live search: debounce so we don't fire a request on every keystroke.
    useEffect(() => {
        const trimmed = searchQuery.trim();
        if (!trimmed) {
            setSearchResults([]);
            setSearchLoading(false);
            return;
        }
        setSearchLoading(true);
        const timer = setTimeout(() => runSearch(trimmed), 350);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery]);

    // Close the results dropdown when clicking outside the search box.
    useEffect(() => {
        const handleOutsideClick = (e) => {
            if (searchWrapRef.current && !searchWrapRef.current.contains(e.target)) {
                setSearchOpen(false);
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, []);

    const runSearch = async (query) => {
        try {
            const { data } = await axios.post(ADMIN_SEARCH_API, {
                adminUserId,
                adminToken,
                query,
            });
            if (data.success) {
                setSearchResults(data.results);
            }
        } catch (err) {
            setSearchResults([]);
        }
        setSearchLoading(false);
        setSearchOpen(true);
    };

    const fetchOverview = async ({ range, page: pageArg, limit, includeDemoUsers }) => {
        setIsLoading(true);
        try {
            const { data } = await axios.post(ADMIN_USAGE_OVERVIEW_API, {
                adminUserId: adminUserId,
                adminToken: adminToken,
                from: range?.from ? range.from.toISOString() : undefined,
                to: range?.to ? range.to.toISOString() : undefined,
                page: pageArg,
                limit,
                includeDemoUsers,
            });

            if (data.success) {
                // Belt-and-suspenders: the backend is expected to exclude demo
                // rows itself when includeDemoUsers is false, but filter here
                // too so the toggle still works against a backend that hasn't
                // picked up that filter yet.
                const rows = includeDemoUsers
                    ? data.admins
                    : data.admins.filter((row) => !isDemoAdminId(row.adminId));
                setAdmins(rows);
                setTotal(data.total ?? 0);
                setPage(data.page ?? pageArg);
                setPerPage(data.limit ?? limit);
            } else {
                setError({
                    errorStatus: true,
                    bgColor: 'alert alert-danger alert-dismissible',
                    errorMessage: data.error || 'Failed to fetch admin usage overview'
                });
            }
        } catch (err) {
            setError({
                errorStatus: true,
                bgColor: 'alert alert-danger alert-dismissible',
                errorMessage: err.response?.data?.error || 'Something went wrong while fetching admin usage overview'
            });
        }
        setIsLoading(false);
    };

    const handlePresetChange = (option) => {
        const nextPreset = option.value;
        setPreset(nextPreset);
        if (nextPreset !== 'custom') {
            const range = resolvePresetRange(nextPreset);
            setActiveRange(range);
            setResetToken(t => t + 1);
            fetchOverview({ range, page: 1, limit: perPage, includeDemoUsers: showDemoUsers });
        }
    };

    const handleCustomSearch = () => {
        const range = { from: customFrom, to: customTo };
        setActiveRange(range);
        setResetToken(t => t + 1);
        fetchOverview({ range, page: 1, limit: perPage, includeDemoUsers: showDemoUsers });
    };

    const handleShowDemoToggle = (e) => {
        const checked = e.target.checked;
        setShowDemoUsers(checked);
        setResetToken(t => t + 1);
        fetchOverview({ range: activeRange, page: 1, limit: perPage, includeDemoUsers: checked });
    };

    const handleChangePage = (nextPage) => {
        fetchOverview({ range: activeRange, page: nextPage, limit: perPage, includeDemoUsers: showDemoUsers });
    };

    const handleChangeRowsPerPage = (nextPerPage, nextPage) => {
        fetchOverview({ range: activeRange, page: nextPage, limit: nextPerPage, includeDemoUsers: showDemoUsers });
    };

    const handleToggleAiBot = async (row, nextEnabled) => {
        setTogglingId(row.adminId);
        try {
            const { data } = await axios.post(AI_BOT_TOGGLE_API, {
                adminUserId,
                adminToken,
                userId: row.adminId,
                status: nextEnabled ? 1 : 0,
            });

            if (data.success) {
                const applyUpdate = (a) => (a.adminId === row.adminId ? { ...a, aiBotEnabled: nextEnabled } : a);
                setAdmins((prev) => prev.map(applyUpdate));
                setSearchResults((prev) => prev.map(applyUpdate));
            } else {
                setError({
                    errorStatus: true,
                    bgColor: 'alert alert-danger alert-dismissible',
                    errorMessage: data.error || 'Failed to update AI Bot status'
                });
            }
        } catch (err) {
            setError({
                errorStatus: true,
                bgColor: 'alert alert-danger alert-dismissible',
                errorMessage: err.response?.data?.error || 'Something went wrong while updating AI Bot status'
            });
        }
        setTogglingId(null);
    };

    const renderStatusToggle = (row) => isDemoAdminId(row.adminId)
        ? <span className='text-muted'>—</span>
        : (
            <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                <label className={switchStyles.switch}>
                    <input
                        type="checkbox"
                        checked={row.aiBotEnabled}
                        disabled={togglingId === row.adminId}
                        onChange={(e) => handleToggleAiBot(row, e.target.checked)}
                    />
                    <span className={switchStyles.slider}></span>
                </label>
                <span className={row.aiBotEnabled ? 'text-success' : 'text-danger'} style={{ fontSize: '12px', fontWeight: 600 }}>
                    {row.aiBotEnabled ? 'Active' : 'Inactive'}
                </span>
            </div>
        );

    const columns = [
        {
            name: 'Admin ID',
            selector: row => row.adminId,
            cell: row => <span style={{ color: c.text, fontWeight: 600 }}>{row.adminId}</span>,
            minWidth: '110px',
            sortable: true,
        },
        {
            name: 'Username',
            selector: row => row.username || '—',
            cell: row => <span style={{ color: c.textMuted }}>{row.username || '—'}</span>,
            minWidth: '200px',
            sortable: true,
        },
        {
            name: 'Total Requests',
            selector: row => row.totalRequests,
            right: true,
            minWidth: '140px',
            sortable: true,
        },
        {
            name: 'WhatsApp Requests',
            selector: row => row.whatsappRequests,
            right: true,
            minWidth: '160px',
            sortable: true,
        },
        {
            name: 'Error Requests',
            selector: row => row.errorRequests,
            // Same red signal as the Errors tab — a nonzero count here is the
            // one thing on this table worth the eye catching first.
            cell: row => (
                <span style={{
                    color: row.errorRequests > 0 ? c.signal : c.textFaint,
                    fontWeight: row.errorRequests > 0 ? 700 : 400,
                    background: row.errorRequests > 0 ? c.signalBg : 'transparent',
                    borderRadius: '999px',
                    padding: row.errorRequests > 0 ? '2px 10px' : 0,
                }}>
                    {row.errorRequests}
                </span>
            ),
            right: true,
            minWidth: '140px',
            sortable: true,
        },
        {
            name: 'Total Tokens',
            selector: row => row.totalTokens,
            right: true,
            minWidth: '130px',
            sortable: true,
        },
        {
            name: 'Avg Latency',
            cell: row => `${row.avgLatencyMs} ms`,
            right: true,
            minWidth: '130px',
            sortable: true,
            sortFunction: (a, b) => a.avgLatencyMs - b.avgLatencyMs,
        },
        {
            name: 'Status',
            cell: renderStatusToggle,
            minWidth: '160px',
        },
    ];

    return (
        <div className="wrapper">
            <AdminLeftMenu />
            <div className="main">
                <AdminHeader />

                <main className="content">
                    <div className="container-fluid p-0">
                        <div className="row mb-3 mb-xl-3 align-items-center">
                            <div className="col-auto d-none d-sm-block">
                                <h3 className="mt-2"><strong>{activeTab === 'usage' ? 'AI Bot Usage Overview' : 'AI Bot Error Logs'}</strong></h3>
                            </div>
                        </div>

                        <ul className="nav nav-tabs mb-3">
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link btn btn-link ${activeTab === 'usage' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('usage')}
                                >
                                    Usage
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link btn btn-link ${activeTab === 'errors' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('errors')}
                                >
                                    Errors
                                </button>
                            </li>
                        </ul>

                        {activeTab === 'usage' && (
                            <>
                                <div className="row mb-3 align-items-center">
                                    <div className="col-auto ml-auto">
                                        <div className={styles.toolbar}>
                                            <div className={styles.adminSearch} ref={searchWrapRef}>
                                                <div className={styles.adminSearchInputWrap}>
                                                    <FontAwesomeIcon icon={faSearch} className={styles.adminSearchIcon} />
                                                    <input
                                                        type="text"
                                                        className={styles.adminSearchInput}
                                                        placeholder="Search admin ID or email"
                                                        value={searchQuery}
                                                        onChange={(e) => setSearchQuery(e.target.value)}
                                                        onFocus={() => searchQuery.trim() && setSearchOpen(true)}
                                                    />
                                                </div>

                                                {searchOpen && (
                                                    <div className={styles.adminSearchDropdown}>
                                                        {searchLoading && (
                                                            <div className={styles.adminSearchEmpty}>Searching...</div>
                                                        )}
                                                        {!searchLoading && searchResults.length === 0 && (
                                                            <div className={styles.adminSearchEmpty}>No admin found</div>
                                                        )}
                                                        {!searchLoading && searchResults.map((row) => (
                                                            <div className={styles.adminSearchRow} key={row.adminId}>
                                                                <div className={styles.adminSearchMeta}>
                                                                    <span className={styles.adminSearchId}>{row.adminId}</span>
                                                                    <span className={styles.adminSearchEmail}>{row.username || '—'}</span>
                                                                </div>
                                                                <div className={styles.adminSearchStatus}>
                                                                    {renderStatusToggle(row)}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>

                                            <label className={styles.demoToggle}>
                                                <span className={switchStyles.switch}>
                                                    <input
                                                        type="checkbox"
                                                        checked={showDemoUsers}
                                                        onChange={handleShowDemoToggle}
                                                    />
                                                    <span className={switchStyles.slider}></span>
                                                </span>
                                                Demo Users
                                            </label>

                                            <div className={styles.rangeSelect}>
                                                <Select
                                                    options={RANGE_PRESETS}
                                                    value={RANGE_PRESETS.find(o => o.value === preset)}
                                                    onChange={handlePresetChange}
                                                    styles={selectStyles}
                                                    isSearchable={false}
                                                    menuPortalTarget={document.body}
                                                />
                                            </div>

                                            {preset === 'custom' && (
                                                <>
                                                    <div className={styles.dateField}>
                                                        <DatePicker
                                                            className="form-control"
                                                            dateFormat="yyyy/MM/dd"
                                                            onChange={setCustomFrom}
                                                            selected={customFrom}
                                                            placeholderText='From Date'
                                                        />
                                                    </div>

                                                    <div className={styles.dateField}>
                                                        <DatePicker
                                                            className="form-control"
                                                            dateFormat="yyyy/MM/dd"
                                                            onChange={setCustomTo}
                                                            selected={customTo}
                                                            placeholderText='To Date'
                                                        />
                                                    </div>

                                                    <button
                                                        type="button"
                                                        onClick={handleCustomSearch}
                                                        disabled={!customFrom || !customTo}
                                                        className={`btn btn-primary ${styles.searchBtn}`}
                                                    >
                                                        <FontAwesomeIcon icon={faSearch} />
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {error.errorStatus && (
                                    <div className={error.bgColor} role="alert">
                                        <button type="button" className="close" data-dismiss="alert" aria-label="Close">
                                            <span aria-hidden="true">×</span>
                                        </button>
                                        <div>{error.errorMessage}</div>
                                    </div>
                                )}

                                <div style={modernCardStyle}>
                                    <div style={{ overflowX: 'auto' }}>
                                        <DataTable
                                            key={resetToken}
                                            columns={columns}
                                            data={admins}
                                            customStyles={modernTableStyles}
                                            highlightOnHover
                                            pagination
                                            paginationServer
                                            paginationTotalRows={total}
                                            paginationDefaultPage={page}
                                            paginationPerPage={perPage}
                                            paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                            onChangePage={handleChangePage}
                                            onChangeRowsPerPage={handleChangeRowsPerPage}
                                            noDataComponent={
                                                <div style={{ padding: '32px', color: c.textMuted, fontSize: '13px' }}>
                                                    {isLoading ? 'Loading...' : 'No data'}
                                                </div>
                                            }
                                        />
                                    </div>
                                </div>
                            </>
                        )}

                        {activeTab === 'errors' && <AiBotErrorLogs />}
                    </div>
                </main>
            </div>
        </div>
    );
};

export default AiBotUsageOverview;
